'use client';

import { Slot } from '@radix-ui/react-slot';
import {
   type CSSProperties,
   type ComponentProps,
   type ReactNode,
   type RefObject,
   createContext,
   use,
   useCallback,
   useId,
   useLayoutEffect,
   useMemo,
   useRef,
   useState,
} from 'react';
import { invariant } from '@ifixit/helpers';
import { cn } from '@ifixit/ui/lib/utils';
import { useControllableState } from '../hooks/use-controllable-state';
import { useStableCallback } from '../hooks/use-stable-callback';
import { afterExit } from '../lib/after-exit';
import { assignRef } from '../lib/assign-ref';
import { takeBodyStyles } from '../lib/body-styles';
import { focusControl, focusFirst, isFocusable, tabbables } from '../lib/focus';

/**
 * A non-modal dialog anchored to its trigger. The content renders in place,
 * where the JSX puts it, and the Popover API lifts it into the top layer: it
 * stays inside whatever dialog, sheet, drawer or form owns the trigger, so
 * their focus traps and outside-press checks treat it as theirs, while it
 * escapes their clipping and transforms.
 *
 * Like Radix's, the styled content sits in an unstyled positioner. The
 * positioner takes the body's inherited styles, so the content looks the same
 * wherever it renders, and it places the content's margin box. While open it
 * is one more child next to the trigger, so a parent that spaces its children
 * by :last-child (space-y, divide-y, last:) spaces the trigger too; wrap the
 * Popover or use gap there.
 */

interface PopoverContextValue {
   open: boolean;
   setOpen: (open: boolean) => void;
   contentId: string;
   triggerId: string;
   triggerRef: RefObject<Element | null>;
}

const PopoverContext = createContext<PopoverContextValue | null>(null);
const openPopoverContents: HTMLDivElement[] = [];

/**
 * Handed to `onInteractOutside` for a press or a focus move outside the
 * trigger and the content. A touch reports the tap's click, not the press.
 * preventDefault keeps the popover open.
 */
type PopoverInteractOutsideEvent = CustomEvent<{
   originalEvent: PointerEvent | MouseEvent | FocusEvent;
}>;

interface PopoverProps {
   open?: boolean;
   defaultOpen?: boolean;
   onOpenChange?: (open: boolean) => void;
   children?: ReactNode;
}

function Popover({ open: openProp, defaultOpen = false, onOpenChange, children }: PopoverProps) {
   // The hook's own onChange only fires in controlled mode; setOpen below
   // notifies uncontrolled consumers too.
   const [open, setOpenState] = useControllableState({ prop: openProp, defaultProp: defaultOpen });
   const contentId = useId();
   const triggerId = useId();
   const triggerRef = useRef<Element | null>(null);

   const setOpen = useStableCallback((next: boolean) => {
      if (next === open) return;
      setOpenState(next);
      onOpenChange?.(next);
   });

   const context = useMemo(
      () => ({ open, setOpen, contentId, triggerId, triggerRef }),
      [open, setOpen, contentId, triggerId]
   );
   return <PopoverContext value={context}>{children}</PopoverContext>;
}

function PopoverTrigger({
   asChild = false,
   ref,
   onClick,
   ...props
}: ComponentProps<'button'> & { asChild?: boolean }) {
   const { open, setOpen, contentId, triggerId, triggerRef } = usePopover();
   const setRefs = useCallback(
      (node: HTMLButtonElement | null) => {
         triggerRef.current = node;
         assignRef(ref, node);
      },
      [triggerRef, ref]
   );
   const Comp = asChild ? Slot : 'button';
   return (
      <Comp
         ref={setRefs}
         // A caller's id, or an asChild element's own, wins; the content reads
         // whichever lands on the element.
         id={triggerId}
         type="button"
         aria-haspopup="dialog"
         aria-expanded={open}
         aria-controls={open ? contentId : undefined}
         data-state={open ? 'open' : 'closed'}
         data-slot="popover-trigger"
         onClick={event => {
            onClick?.(event);
            if (!event.defaultPrevented) setOpen(!open);
         }}
         {...props}
      />
   );
}

type PopoverSide = 'top' | 'bottom' | 'left' | 'right';
type PopoverAlign = 'start' | 'center' | 'end';

interface PopoverPlacement {
   /** @deprecated Put a `popover-top` or `popover-bottom` class on PopoverContent; the classes also take `left`, `right` and breakpoints (`md:popover-left`). */
   side?: 'top' | 'bottom';
   /** @deprecated Put a `popover-align-start`, `-center` or `-end` class on PopoverContent. */
   align?: PopoverAlign;
   sideOffset?: number;
   /** Distance kept from the viewport edges. */
   collisionPadding?: number;
   /** False keeps the preferred side even when the other one has more room. */
   avoidCollisions?: boolean;
}

interface PopoverContentProps extends ComponentProps<'div'>, PopoverPlacement {
   /** Runs on open, before focus moves into the content. preventDefault keeps focus where it is. */
   onOpenAutoFocus?: (event: Event) => void;
   /** Runs on close when focus would be lost with the content, before it returns to the trigger. preventDefault leaves focus alone. */
   onCloseAutoFocus?: (event: Event) => void;
   onInteractOutside?: (event: PopoverInteractOutsideEvent) => void;
}

function PopoverContent({
   className,
   side = 'bottom',
   align = 'center',
   sideOffset = 4,
   collisionPadding = 0,
   avoidCollisions = true,
   onOpenAutoFocus,
   onCloseAutoFocus,
   onInteractOutside,
   ref,
   children,
   ...props
}: PopoverContentProps) {
   const { open, setOpen, contentId, triggerRef } = usePopover();
   const [mounted, setMounted] = useState(open);
   if (open && !mounted) setMounted(true);

   const positionerRef = useRef<HTMLDivElement>(null);
   const contentRef = useRef<HTMLDivElement | null>(null);
   const dismissedOutsideRef = useRef(false);
   const pointerDownOutsideRef = useRef(false);
   const heldFocusRef = useRef(false);
   const setRefs = useCallback(
      (node: HTMLDivElement | null) => {
         contentRef.current = node;
         assignRef(ref, node);
      },
      [ref]
   );

   // A layout effect, not useStableCallback: that one updates after paint, so
   // the first placement would still see the previous render's props.
   const optionsRef = useRef({
      placement: { side, align, sideOffset, collisionPadding, avoidCollisions },
      onOpenAutoFocus,
      onCloseAutoFocus,
      onInteractOutside,
   });
   useLayoutEffect(() => {
      optionsRef.current = {
         placement: { side, align, sideOffset, collisionPadding, avoidCollisions },
         onOpenAutoFocus,
         onCloseAutoFocus,
         onInteractOutside,
      };
   });

   useLayoutEffect(() => {
      const positioner = positionerRef.current;
      const content = contentRef.current;
      const trigger = triggerRef.current;
      if (!open || !positioner || !content || !trigger) return;
      dismissedOutsideRef.current = false;
      pointerDownOutsideRef.current = false;
      heldFocusRef.current = false;

      function reposition() {
         if (positioner && content && trigger) {
            placeContent(positioner, content, trigger, optionsRef.current.placement);
         }
      }
      function isInside(target: EventTarget | null) {
         return (
            target instanceof Node && (!!content?.contains(target) || !!trigger?.contains(target))
         );
      }
      function dismissOutside(originalEvent: MouseEvent | FocusEvent) {
         if (isInside(originalEvent.target)) return;
         const followsPointerDown =
            originalEvent.type === 'focusin' && pointerDownOutsideRef.current;
         const event: PopoverInteractOutsideEvent = new CustomEvent('popover.interactOutside', {
            cancelable: true,
            detail: { originalEvent },
         });
         optionsRef.current.onInteractOutside?.(event);
         if (event.defaultPrevented) return;
         // A press on a focusable control sends pointerdown and then focusin.
         // Report both to the consumer, as Radix did, but ask to close once.
         if (followsPointerDown) return;
         if (originalEvent.type === 'pointerdown') {
            pointerDownOutsideRef.current = true;
            queueMicrotask(() => {
               pointerDownOutsideRef.current = false;
            });
         }
         dismissedOutsideRef.current = true;
         setOpen(false);
      }
      // A touch outside may start a scroll, so it closes on the tap's click,
      // as Radix did; a scroll sends no click.
      function handlePointerDown(event: PointerEvent) {
         document.removeEventListener('click', dismissOutside, { capture: true });
         if (event.pointerType !== 'touch') {
            dismissOutside(event);
         } else if (!isInside(event.target)) {
            document.addEventListener('click', dismissOutside, { capture: true, once: true });
         }
      }
      function handleFocusIn(event: FocusEvent) {
         if (event.target instanceof Node && content?.contains(event.target)) {
            heldFocusRef.current = true;
         }
         dismissOutside(event);
      }
      // Checked every frame rather than on scroll and resize events: a
      // trigger also moves with layout shifts and transforms (a schematic
      // panned under its hotspot) that fire neither, and Radix followed
      // those too. The cost is one rect read per open popover per frame. It
      // runs before the frame's own layout, so it moves that layout earlier
      // rather than adding one, and it places again only on a change.
      let frame = 0;
      let placedFor = '';
      function followTrigger() {
         if (!trigger) return;
         const key = placementKey(trigger, content, optionsRef.current.placement);
         if (key !== placedFor) {
            placedFor = key;
            reposition();
         }
         frame = requestAnimationFrame(followTrigger);
      }
      // Dialogs, drawers and sheets listen for Escape on the document; the
      // window sees it first, so the key closes only the popover. When focus
      // sits outside it, the key still reaches whoever holds focus (the
      // header search clears on it), marked handled as Radix did, which
      // keeps the dialog, drawer or sheet around it open.
      function handleKeyDown(event: KeyboardEvent) {
         if (event.key !== 'Escape' || event.isComposing) return;
         // Sibling popovers all hear the window event. Only the one the
         // browser put on the top layer last should close.
         if (openPopoverContents.at(-1) !== content) return;
         // A popup open inside this one closes first.
         if (content?.querySelector('[popover][data-state="open"]')) return;
         setOpen(false);
         event.preventDefault();
         const focused = document.activeElement;
         if (focused && focused !== document.body && !isInside(focused)) return;
         event.stopPropagation();
      }

      // Radix's loop: Tab past the last tabbable wraps to the first and
      // Shift+Tab back; with nothing tabbable, focus stays on the content.
      function loopTab(event: KeyboardEvent) {
         if (event.key !== 'Tab' || event.altKey || event.ctrlKey || event.metaKey) return;
         // A popover open inside this one loops its own Tab.
         const owner =
            event.target instanceof Element &&
            event.target.closest('[data-slot="popover-content"]');
         if (!content || owner !== content) return;
         const focused = document.activeElement;
         const tabbable = tabbables(content);
         const first = tabbable[0];
         const last = tabbable.at(-1);
         if (!first || !last) {
            if (focused === content) event.preventDefault();
         } else if (!event.shiftKey && focused === last) {
            event.preventDefault();
            focusControl(first);
         } else if (event.shiftKey && focused === first) {
            event.preventDefault();
            focusControl(last);
         }
      }

      // Radix left the dialog unnamed; the trigger names it unless the caller
      // did.
      if (!content.hasAttribute('aria-label') && !content.hasAttribute('aria-labelledby')) {
         content.setAttribute('aria-labelledby', trigger.id);
      }
      takeBodyStyles(positioner);
      positioner.showPopover?.();
      openPopoverContents.push(content);
      followTrigger();

      const contentResize =
         typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(reposition);
      contentResize?.observe(content);
      window.addEventListener('keydown', handleKeyDown, { capture: true });
      content.addEventListener('keydown', loopTab);
      document.addEventListener('pointerdown', handlePointerDown, { capture: true });
      document.addEventListener('focusin', handleFocusIn);

      const openFocus = new Event('popover.openAutoFocus', { cancelable: true });
      optionsRef.current.onOpenAutoFocus?.(openFocus);
      if (!openFocus.defaultPrevented && !content.contains(document.activeElement)) {
         focusFirst(content);
      }

      return () => {
         const stackIndex = openPopoverContents.lastIndexOf(content);
         if (stackIndex !== -1) openPopoverContents.splice(stackIndex, 1);
         cancelAnimationFrame(frame);
         contentResize?.disconnect();
         window.removeEventListener('keydown', handleKeyDown, { capture: true });
         content.removeEventListener('keydown', loopTab);
         document.removeEventListener('pointerdown', handlePointerDown, { capture: true });
         document.removeEventListener('click', dismissOutside, { capture: true });
         document.removeEventListener('focusin', handleFocusIn);
         positioner.hidePopover?.();
      };
   }, [open, setOpen, triggerRef]);

   // Not in the cleanup above: React's commit puts focus back on the element
   // that held it before, so focus can only move once the commit is done.
   useLayoutEffect(() => {
      if (open || !mounted) return;
      const content = contentRef.current;
      const trigger = triggerRef.current;
      const focused = document.activeElement;
      // Focus inside would vanish with the content; focus that already left
      // it for nowhere (the page lost it) is brought back too. A press or a
      // focus move outside hands focus to wherever that went.
      const focusStranded =
         !!content?.contains(focused) ||
         (heldFocusRef.current && (focused === null || focused === document.body));
      if (!dismissedOutsideRef.current && focusStranded) {
         const closeFocus = new Event('popover.closeAutoFocus', { cancelable: true });
         optionsRef.current.onCloseAutoFocus?.(closeFocus);
         if (!closeFocus.defaultPrevented && trigger && isFocusable(trigger)) {
            trigger.focus({ preventScroll: true });
         }
      }
      return afterExit(content, () => setMounted(false));
   }, [open, mounted, triggerRef]);

   if (!mounted) return null;
   const state = open ? 'open' : 'closed';
   return (
      <div
         ref={positionerRef}
         // "manual": the component owns dismissal, the browser only supplies
         // the top layer.
         popover="manual"
         data-slot="popover-positioner"
         data-state={state}
         style={POSITIONER_STYLE}
         // Stays displayed, and in the top layer, while the content fades out.
         className="overflow-visible bg-transparent transition-[display,overlay] transition-discrete duration-150"
      >
         <div
            ref={setRefs}
            id={contentId}
            role="dialog"
            tabIndex={-1}
            data-slot="popover-content"
            data-state={state}
            className={cn(
               'bg-popover text-popover-foreground relative w-80 min-w-[8rem] max-w-(--popover-available-width) max-h-(--popover-available-height) overflow-x-hidden overflow-y-auto overscroll-contain rounded-xl border p-4 shadow-md',
               'data-[side=bottom]:origin-[var(--popover-anchor-x)_top] data-[side=top]:origin-[var(--popover-anchor-x)_bottom]',
               'data-[side=left]:origin-[right_var(--popover-anchor-y)] data-[side=right]:origin-[left_var(--popover-anchor-y)]',
               'opacity-0 transition-[opacity,scale] duration-150 ease-out motion-safe:scale-95 data-[state=closed]:pointer-events-none',
               'data-[state=open]:opacity-100 data-[state=open]:duration-200 motion-safe:data-[state=open]:scale-none starting:data-[state=open]:opacity-0 motion-safe:starting:data-[state=open]:scale-95',
               className
            )}
            {...props}
         >
            {children}
         </div>
      </div>
   );
}

// Inline, so rules for the children of whatever holds the popover (a field's
// `[&>*]:w-full`) can't size or move it; placeContent sets left and top.
const POSITIONER_STYLE: CSSProperties = {
   position: 'fixed',
   inset: 'auto',
   width: 'max-content',
   height: 'auto',
   minWidth: 0,
   minHeight: 0,
   maxWidth: 'none',
   maxHeight: 'none',
   margin: 0,
   padding: 0,
   border: 0,
   transform: 'none',
   translate: 'none',
   scale: 'none',
   rotate: 'none',
   // Not inherited from whatever holds the popover, which may turn them off.
   pointerEvents: 'auto',
   visibility: 'visible',
};

function usePopover() {
   const context = use(PopoverContext);
   invariant(context, 'Popover parts must be rendered inside a Popover');
   return context;
}

// Everything a placement depends on but the content's size, which a
// ResizeObserver watches: the trigger, the viewport, the placement props and
// the classes that can carry a placement.
function placementKey(
   trigger: Element,
   content: Element | null,
   placement: Required<PopoverPlacement>
) {
   const { left, top, width, height } = trigger.getBoundingClientRect();
   const viewport = window.visualViewport;
   return [
      left,
      top,
      width,
      height,
      viewport?.offsetLeft,
      viewport?.offsetTop,
      viewport?.width ?? window.innerWidth,
      viewport?.height ?? window.innerHeight,
      placement.side,
      placement.align,
      placement.sideOffset,
      placement.collisionPadding,
      placement.avoidCollisions,
      content?.className,
   ].join(',');
}

const POPOVER_SIDES: ReadonlySet<string> = new Set(['top', 'bottom', 'left', 'right']);
const POPOVER_ALIGNS: ReadonlySet<string> = new Set(['start', 'center', 'end']);

interface ResolvedPlacement extends Omit<Required<PopoverPlacement>, 'side' | 'align'> {
   side: PopoverSide;
   align: PopoverAlign;
}

// The popover-* utilities set these properties, so a class wins over the
// props and follows the breakpoint that is active when the content is placed.
function resolvePlacement(
   content: HTMLElement,
   placement: Required<PopoverPlacement>
): ResolvedPlacement {
   const style = getComputedStyle(content);
   const side = style.getPropertyValue('--popover-side').trim();
   const align = style.getPropertyValue('--popover-align').trim();
   return {
      ...placement,
      side: POPOVER_SIDES.has(side) ? (side as PopoverSide) : placement.side,
      align: POPOVER_ALIGNS.has(align) ? (align as PopoverAlign) : placement.align,
   };
}

// Bounds are the visual viewport: what a phone keyboard or pinch zoom leaves
// visible. The positioner is the content's margin box.
function placeContent(
   positioner: HTMLElement,
   content: HTMLElement,
   trigger: Element,
   placement: Required<PopoverPlacement>
) {
   const { side, align, sideOffset, collisionPadding, avoidCollisions } = resolvePlacement(
      content,
      placement
   );
   const rect = trigger.getBoundingClientRect();
   const viewport = window.visualViewport;
   const top = viewport?.offsetTop ?? 0;
   const bottom = top + (viewport?.height ?? window.innerHeight);
   const left = viewport?.offsetLeft ?? 0;
   const right = left + (viewport?.width ?? window.innerWidth);

   positioner.style.setProperty('--popover-trigger-width', `${rect.width}px`);
   if (side === 'left' || side === 'right') {
      placeBeside(
         positioner,
         content,
         rect,
         { top, bottom, left, right },
         {
            side,
            align,
            sideOffset,
            collisionPadding,
            avoidCollisions,
         }
      );
      return;
   }
   positioner.style.setProperty(
      '--popover-available-width',
      `${right - left - 2 * collisionPadding}px`
   );
   // Measured against the left edge, so a shrink-to-fit width never wraps
   // against where the content sat last time.
   positioner.style.left = '0px';

   // scrollHeight is the full height whatever max-height is in force, so
   // measuring never resets how far the content is scrolled.
   const fullHeight = positioner.offsetHeight + content.scrollHeight - content.clientHeight;
   const below = bottom - rect.bottom - sideOffset - collisionPadding;
   const above = rect.top - top - sideOffset - collisionPadding;
   let placedBelow = side === 'bottom';
   if (avoidCollisions) {
      const fitsPreferred = fullHeight <= (placedBelow ? below : above);
      const preferredIsRoomier = placedBelow ? below >= above : above >= below;
      if (!fitsPreferred && !preferredIsRoomier) placedBelow = !placedBelow;
   }
   positioner.style.setProperty(
      '--popover-available-height',
      `${Math.max(placedBelow ? below : above, 0)}px`
   );
   content.dataset.side = placedBelow ? 'bottom' : 'top';
   content.dataset.align = align;
   const height = positioner.offsetHeight;
   const width = positioner.offsetWidth;
   positioner.style.top = `${placedBelow ? rect.bottom + sideOffset : rect.top - sideOffset - height}px`;

   const rtl = getComputedStyle(trigger).direction === 'rtl';
   const alignLeft = align === 'start' ? !rtl : align === 'end' ? rtl : null;
   const aligned =
      alignLeft == null
         ? rect.left + (rect.width - width) / 2
         : alignLeft
           ? rect.left
           : rect.right - width;
   const x = Math.max(left + collisionPadding, Math.min(aligned, right - collisionPadding - width));
   positioner.style.left = `${x}px`;
   // Where the trigger's middle falls on the content: the transform origin,
   // and where an arrow would point.
   const anchorX = rect.left + rect.width / 2 - x - content.offsetLeft;
   positioner.style.setProperty(
      '--popover-anchor-x',
      `${Math.min(Math.max(anchorX, 0), content.offsetWidth)}px`
   );
}

interface ViewportBounds {
   top: number;
   bottom: number;
   left: number;
   right: number;
}

function placeBeside(
   positioner: HTMLElement,
   content: HTMLElement,
   rect: DOMRect,
   viewport: ViewportBounds,
   {
      side,
      align,
      sideOffset,
      collisionPadding,
      avoidCollisions,
   }: ResolvedPlacement & { side: 'left' | 'right' }
) {
   positioner.style.setProperty(
      '--popover-available-height',
      `${viewport.bottom - viewport.top - 2 * collisionPadding}px`
   );
   positioner.style.left = '0px';

   const leftRoom = rect.left - viewport.left - sideOffset - collisionPadding;
   const rightRoom = viewport.right - rect.right - sideOffset - collisionPadding;
   let placedLeft = side === 'left';
   if (avoidCollisions) {
      const fitsPreferred = positioner.offsetWidth <= (placedLeft ? leftRoom : rightRoom);
      const preferredIsRoomier = placedLeft ? leftRoom >= rightRoom : rightRoom >= leftRoom;
      if (!fitsPreferred && !preferredIsRoomier) placedLeft = !placedLeft;
   }
   positioner.style.setProperty(
      '--popover-available-width',
      `${Math.max(placedLeft ? leftRoom : rightRoom, 0)}px`
   );
   content.dataset.side = placedLeft ? 'left' : 'right';
   content.dataset.align = align;

   const width = positioner.offsetWidth;
   const height = positioner.offsetHeight;
   positioner.style.left = `${placedLeft ? rect.left - sideOffset - width : rect.right + sideOffset}px`;
   const aligned =
      align === 'start'
         ? rect.top
         : align === 'end'
           ? rect.bottom - height
           : rect.top + (rect.height - height) / 2;
   const y = Math.max(
      viewport.top + collisionPadding,
      Math.min(aligned, viewport.bottom - collisionPadding - height)
   );
   positioner.style.top = `${y}px`;
   const anchorY = rect.top + rect.height / 2 - y - content.offsetTop;
   positioner.style.setProperty(
      '--popover-anchor-y',
      `${Math.min(Math.max(anchorY, 0), content.offsetHeight)}px`
   );
}

export { Popover, PopoverTrigger, PopoverContent, type PopoverInteractOutsideEvent };
